import { useNavigate, useSearch } from "@tanstack/react-router";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { Badge } from "@/components/ui/badge";

import { useContractsRecord } from "../useContractsRecord";
import { RecordLink } from "./RecordLink";
import { monthLabel, useUpsellDetail, type KpiMonthRange, type KpiUpsellDetail } from "../api";
import type { KpiSearch } from "..";
import type { KpiPeriod } from "./PeriodSelector";
import { fmtArr, fmtUsd0 } from "../format";

export function UpsellDrawer({
  period,
  range,
  periodLabel,
}: {
  period: KpiPeriod;
  range: KpiMonthRange;
  periodLabel: string;
}) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/kpi" });
  const open = search.tile === "upsell";
  const expanded = !!search.expanded;

  const go = (next: Partial<KpiSearch>) =>
    navigate({ to: "/kpi", search: (prev: KpiSearch) => ({ ...prev, ...next }) });

  const detail = useUpsellDetail(period, range, open);

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v && !expanded) go({ tile: undefined, expanded: undefined });
      }}
      expanded={expanded}
      onExpandedChange={(exp) => open && go({ expanded: exp ? true : undefined })}
      onClose={() => {
        if (expanded) go({ expanded: undefined });
        else go({ tile: undefined, expanded: undefined });
      }}
      title="Upsell — replacements + expansion"
      subtitle={periodLabel}
    >
      {detail.isLoading ? (
        <div className="p-6 text-sm text-muted-foreground">Loading…</div>
      ) : !detail.data?.available ? (
        <div className="p-6 text-sm text-muted-foreground">No data for this period.</div>
      ) : (
        <UpsellView data={detail.data} expanded={expanded} />
      )}
    </EntityDrawer>
  );
}

function UpsellView({ data, expanded }: { data: KpiUpsellDetail; expanded: boolean }) {
  const { openContract, openAccount } = useContractsRecord();
  const t = data.totals;
  const contracts = expanded ? (data.contracts ?? []) : (data.contracts ?? []).slice(0, 12);
  const expansion = expanded ? (data.expansion ?? []) : (data.expansion ?? []).slice(0, 8);
  return (
    <div>
      <div className="border-b border-border p-4">
        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
          Total upsell ARR
        </div>
        <div className="text-3xl font-extrabold tabular-nums text-warning">
          {fmtArr(t?.total_arr ?? 0, 0)}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
          <Mini label={`Replacement/upsell Δ (${t?.contracts ?? 0})`} value={fmtUsd0(t?.contract_delta_arr)} />
          <Mini label="Billed expansion" value={fmtUsd0(t?.expansion_arr)} />
        </div>
      </div>

      <div className="p-4">
        <SectionTitle>Contracts</SectionTitle>
        <table className="mt-2 w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border">
              <th className="px-2 py-1.5 text-left font-semibold">Account</th>
              <th className="px-2 py-1.5 text-left font-semibold">Type</th>
              {expanded && <th className="px-2 py-1.5 text-left font-semibold">Signed</th>}
              <th className="px-2 py-1.5 text-right font-semibold">Prior → New</th>
              <th className="px-2 py-1.5 text-right font-semibold">Uplift</th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((c) => (
              <tr key={`${c.qb_id}-${c.signed_month}`} className="border-b border-border/60">
                <td className="px-2 py-1.5">
                  <RecordLink
                    label={c.account_name}
                    onOpen={expanded && c.contract_id != null && (() => openContract(c.contract_id))}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Badge variant={c.kind === "upsell" ? "info" : "warning"}>
                    {c.kind === "upsell" ? "Upsell" : "Replacement"}
                  </Badge>
                </td>
                {expanded && (
                  <td className="px-2 py-1.5 tabular-nums text-muted-foreground">
                    {monthLabel(c.signed_month)}
                  </td>
                )}
                <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                  {/* Only replacements restate a book, so only they have a
                      baseline. An incremental contract lists just what was
                      added, and for a Purchase Order Upsell prior_arr is the
                      *reseller's* whole book — rendering the transition showed
                      "$503,148 → $2,370" for a $2,370 order. The Uplift column
                      already carries the value. */}
                  {c.kind === "upsell" ? "—" : `${fmtArr(c.prior_arr, 0)} → ${fmtArr(c.new_arr, 0)}`}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums font-semibold">
                  <span className={c.uplift_arr >= 0 ? "text-success" : "text-danger"}>
                    {c.uplift_arr >= 0 ? "+" : "−"}
                    {fmtArr(Math.abs(c.uplift_arr), 0)}
                  </span>
                </td>
              </tr>
            ))}
            {contracts.length === 0 && <Empty cols={expanded ? 5 : 4} />}
          </tbody>
        </table>
      </div>

      <div className="border-t border-border p-4">
        <SectionTitle>Billed expansion</SectionTitle>
        <div className="mt-2 flex flex-col divide-y divide-border">
          {expansion.map((e) => (
            <div key={`${e.qb_id}-${e.month}`} className="flex items-center justify-between py-1.5 text-sm">
              <span className="min-w-0 truncate">
                <RecordLink
                  label={e.account_name}
                  onOpen={expanded && e.account_id ? () => openAccount(e.account_id) : undefined}
                />
                {expanded && <span className="ml-1.5 text-[10px] text-muted-foreground">{e.month}</span>}
              </span>
              <span className="tabular-nums font-semibold text-success">+{fmtArr(e.expansion_arr, 0)}</span>
            </div>
          ))}
          {expansion.length === 0 && (
            <div className="py-4 text-center text-sm text-muted-foreground">No billed expansion.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
        {label}
      </div>
      <div className="mt-0.5 font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Empty({ cols }: { cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-2 py-6 text-center text-sm text-muted-foreground">
        None in this period.
      </td>
    </tr>
  );
}
